import type {
  AiStoreyKind,
  PlanCoverage,
  SpecialStoreyKind,
  StoreyCount,
  StoreyDrawing,
  StoreyLevel,
} from '../models/storey-level.model';
import { assertUnreachable } from './function.helpers';

const toIndex = (value: number | null | undefined): number | null =>
  value != null && Number.isInteger(value) ? value : null;

const isContiguous = (numbers: readonly number[]): boolean =>
  numbers.every((n, i) => i === 0 || n === (numbers[i - 1] ?? n) + 1);

/**
 * Level spanning `count` storeys from `fromIndex`: `single` for one storey,
 * `range` for several.
 */
const spanLevel = (fromIndex: number, count: number): StoreyLevel =>
  count > 1
    ? { kind: 'range', fromIndex, toIndex: fromIndex + count - 1 }
    : { kind: 'single', index: fromIndex };

/**
 * Narrow a flat AI storey row to a {@link StoreyLevel}. `index` is the
 * model's position relative to its entrance. A printed plan range with an
 * index becomes a `range`; a `typical` row without an index stays `typical`.
 * Null when a regular storey has no index.
 */
export const storeyLevelFromAi = (row: {
  kind: AiStoreyKind;
  index: number | null;
  planNumbers: readonly number[];
}): StoreyLevel | null => {
  const index = toIndex(row.index);
  const span = isContiguous(row.planNumbers)
    ? Math.max(1, row.planNumbers.length)
    : 1;
  switch (row.kind) {
    case 'roof':
      return { kind: 'roof' };
    case 'typical':
      return index === null || span === 1
        ? { kind: 'typical' }
        : spanLevel(index, span);
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
      return { kind: row.kind, index };
    case 'regular':
      return index === null ? null : spanLevel(index, span);
    default:
      return assertUnreachable(row.kind);
  }
};

/**
 * Re-index a storey from its printed plan numbers once the entrance plan is
 * known. Keeps the storey kind (an attic stays an attic). Non-contiguous
 * plan lists and rows without plan numbers keep their level.
 */
export const placeStoreyLevel = (input: {
  level: StoreyLevel | null;
  planNumbers: readonly number[];
  entrancePlanNumber: number;
}): StoreyLevel | null => {
  const first = input.planNumbers[0];
  if (first === undefined || !isContiguous(input.planNumbers)) {
    return input.level;
  }
  const fromIndex = first - input.entrancePlanNumber;
  const count = input.planNumbers.length;
  const kind = input.level?.kind;
  switch (kind) {
    case 'roof':
      return input.level;
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
      return count === 1 ? { kind, index: fromIndex } : input.level;
    case 'single':
    case 'range':
    case 'typical':
    case undefined:
      return spanLevel(fromIndex, count);
    default:
      return assertUnreachable(kind);
  }
};

/** Level for a special storey the label names, with an unknown index. */
export const specialStoreyLevel = (kind: SpecialStoreyKind): StoreyLevel => ({
  kind,
  index: null,
});

/**
 * Lowest index the level occupies. Null for `typical`, `roof` and special
 * storeys that are not placed.
 */
export const storeyLevelIndex = (level: StoreyLevel | null): number | null => {
  if (level === null) return null;
  switch (level.kind) {
    case 'single':
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
      return level.index;
    case 'range':
      return level.fromIndex;
    case 'typical':
    case 'roof':
      return null;
    default:
      return assertUnreachable(level);
  }
};

/** True on the entrance storey (index 0, full or suterräng). */
export const isEntranceStoreyLevel = (level: StoreyLevel | null): boolean =>
  (level?.kind === 'single' || level?.kind === 'split_level') &&
  level.index === 0;

const TYPICAL_SORT_OFFSET = 0.5;

/**
 * Bottom-to-top sort key. A `typical` floor sits just above the entrance,
 * `roof` on top, unplaced storeys last.
 */
export const storeyLevelSortKey = (level: StoreyLevel | null): number => {
  if (level === null) return Number.POSITIVE_INFINITY;
  switch (level.kind) {
    case 'typical':
      return TYPICAL_SORT_OFFSET;
    case 'roof':
      return Number.MAX_SAFE_INTEGER;
    case 'single':
    case 'range':
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
      return storeyLevelIndex(level) ?? Number.MAX_SAFE_INTEGER - 1;
    default:
      return assertUnreachable(level);
  }
};

/**
 * Position part of a level, without the storey kind: what facit `index`
 * asserts. `index` for full and placed special storeys, `range`, `typical`,
 * `roof`, or null when not placed.
 */
export type StoreyLevelPosition =
  | { kind: 'index'; index: number }
  | { kind: 'range'; fromIndex: number; toIndex: number }
  | { kind: 'typical' }
  | { kind: 'roof' };

export const storeyLevelPosition = (
  level: StoreyLevel | null,
): StoreyLevelPosition | null => {
  if (level === null) return null;
  switch (level.kind) {
    case 'single':
      return { kind: 'index', index: level.index };
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
      return level.index === null
        ? null
        : { kind: 'index', index: level.index };
    case 'range':
      return {
        kind: 'range',
        fromIndex: level.fromIndex,
        toIndex: level.toIndex,
      };
    case 'typical':
      return { kind: 'typical' };
    case 'roof':
      return { kind: 'roof' };
    default:
      return assertUnreachable(level);
  }
};

/** Stable string for a {@link StoreyLevelPosition}: `i:4`, `r:1-4`, `typical`, `roof`. */
export const storeyLevelPositionKey = (
  position: StoreyLevelPosition | null,
): string | null => {
  if (position === null) return null;
  switch (position.kind) {
    case 'index':
      return `i:${position.index}`;
    case 'range':
      return `r:${position.fromIndex}-${position.toIndex}`;
    case 'typical':
    case 'roof':
      return position.kind;
    default:
      return assertUnreachable(position);
  }
};

/** Indexes a position occupies; empty for `typical` and `roof`. */
export const storeyLevelPositionIndexes = (
  position: StoreyLevelPosition | null,
): number[] => {
  if (position === null) return [];
  switch (position.kind) {
    case 'index':
      return [position.index];
    case 'range':
      return Array.from(
        { length: position.toIndex - position.fromIndex + 1 },
        (_, i) => position.fromIndex + i,
      );
    case 'typical':
    case 'roof':
      return [];
    default:
      return assertUnreachable(position);
  }
};

/** A storey as {@link resolveStoreyDrawings} sees it. */
export type DrawableStorey = {
  position: StoreyLevelPosition | null;
  kind: SpecialStoreyKind | 'roof' | 'regular';
  /** Asserted drawing state; undefined resolves from the plans. */
  drawing?: StoreyDrawing;
};

/**
 * Whether the normalplan may stand for a storey: above the entrance, not an
 * attic, placed (or the `typical` row itself).
 */
const normalplanMayCover = (storey: DrawableStorey): boolean => {
  if (storey.kind === 'attic' || storey.kind === 'roof') return false;
  if (storey.position?.kind === 'typical') return true;
  const indexes = storeyLevelPositionIndexes(storey.position);
  return indexes.length > 0 && indexes.every((index) => index >= 1);
};

/**
 * Drawing state per storey (same order as `storeys`) from the plans that
 * cover the volume: `own` when a specific plan covers one of its indexes;
 * otherwise `normalplan` when a `remaining` plan exists and the storey is
 * above the entrance and not an attic; otherwise `data_gap`. An asserted
 * state wins, so a storey stated as `data_gap` is never filled.
 */
/**
 * Coverages that decide whether a storey has its own sheet.
 * A roof sheet stays a roof plan: its coverage is not rewritten to an index.
 * When that sheet prints one plan number, the storey with that number has
 * its own sheet, so the normalplan does not stand for it.
 */
export const coveragesForStoreyDrawings = (
  drawings: readonly {
    coverage: PlanCoverage | null;
    planNumbers: readonly number[];
  }[],
  storeys: readonly {
    planNumbers: readonly number[];
    level: StoreyLevel | null;
  }[],
): PlanCoverage[] => {
  const indexByPlan = new Map<number, number>();
  for (const storey of storeys) {
    if (storey.planNumbers.length !== 1) continue;
    const plan = storey.planNumbers[0];
    const index = storeyLevelIndex(storey.level);
    if (plan === undefined || index == null) continue;
    indexByPlan.set(plan, index);
  }
  const plans: PlanCoverage[] = [];
  for (const drawing of drawings) {
    if (drawing.coverage != null) plans.push(drawing.coverage);
    if (
      drawing.coverage?.kind !== 'specific' ||
      drawing.coverage.level.kind !== 'roof' ||
      drawing.planNumbers.length !== 1
    ) {
      continue;
    }
    const plan = drawing.planNumbers[0];
    if (plan === undefined) continue;
    const index = indexByPlan.get(plan);
    if (index == null) continue;
    plans.push({ kind: 'specific', level: { kind: 'single', index } });
  }
  return plans;
};

export const resolveStoreyDrawings = (input: {
  storeys: readonly DrawableStorey[];
  plans: readonly PlanCoverage[];
}): StoreyDrawing[] => {
  const specific = new Set(
    input.plans.flatMap((plan) =>
      plan.kind === 'specific'
        ? storeyLevelPositionIndexes(storeyLevelPosition(plan.level))
        : [],
    ),
  );
  const hasNormalplan = input.plans.some((plan) => plan.kind === 'remaining');
  return input.storeys.map((storey) => {
    if (storey.drawing !== undefined) return storey.drawing;
    const indexes = storeyLevelPositionIndexes(storey.position);
    if (indexes.some((index) => specific.has(index))) return 'own';
    return hasNormalplan && normalplanMayCover(storey)
      ? 'normalplan'
      : 'data_gap';
  });
};

/** A {@link DrawableStorey} for a stored level; the kind comes from the level. */
export const drawableStorey = (
  level: StoreyLevel | null,
  drawing?: StoreyDrawing,
): DrawableStorey => ({
  position: storeyLevelPosition(level),
  kind: storeyLevelStoreyKind(level),
  ...(drawing === undefined ? {} : { drawing }),
});

const STOREY_COUNT_KINDS: Record<
  SpecialStoreyKind | 'roof' | 'regular',
  boolean
> = {
  regular: true,
  attic: true,
  technical: true,
  split_level: true,
  mezzanine: false,
  roof: false,
};

/**
 * Storey count by convention: distinct indexes from the entrance (0) up,
 * attic and technical top floors included; below ground, entresol and
 * takplan not. Null when a `typical` row or an unplaced storey leaves the
 * count open.
 */
export const countStoreys = (
  levels: readonly (StoreyLevel | null)[],
): number | null => {
  const indexes = new Set<number>();
  for (const level of levels) {
    const kind = storeyLevelStoreyKind(level);
    if (!STOREY_COUNT_KINDS[kind]) continue;
    const position = storeyLevelPosition(level);
    if (position === null || position.kind === 'typical') return null;
    for (const index of storeyLevelPositionIndexes(position)) {
      if (index >= 0) indexes.add(index);
    }
  }
  return indexes.size === 0 ? null : indexes.size;
};

/**
 * Indexes the normalplan stands for: placed storeys that resolve to
 * `normalplan`. `includesTypical` when a `typical` storey row resolves to it.
 */
export const normalplanCoverage = (input: {
  storeys: readonly DrawableStorey[];
  plans: readonly PlanCoverage[];
}): { indexes: number[]; includesTypical: boolean } => {
  const drawings = resolveStoreyDrawings(input);
  const covered = input.storeys.filter((_, i) => drawings[i] === 'normalplan');
  return {
    indexes: [
      ...new Set(
        covered.flatMap((s) => storeyLevelPositionIndexes(s.position)),
      ),
    ].sort((a, b) => a - b),
    includesTypical: covered.some((s) => s.position?.kind === 'typical'),
  };
};

/** `regular` for full storeys, ranges and typical floors; the special kind otherwise. */
export const storeyLevelStoreyKind = (
  level: StoreyLevel | null,
): SpecialStoreyKind | 'roof' | 'regular' => {
  if (level === null) return 'regular';
  switch (level.kind) {
    case 'single':
    case 'range':
    case 'typical':
      return 'regular';
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level':
    case 'roof':
      return level.kind;
    default:
      return assertUnreachable(level);
  }
};

const formatSignedIndex = (index: number): string => {
  if (index > 0) return `+${index}`;
  if (index < 0) return `−${Math.abs(index)}`;
  return '0';
};

const SPECIAL_STOREY_LABEL: Record<SpecialStoreyKind, string> = {
  attic: 'vind',
  technical: 'teknik',
  mezzanine: 'entresol',
  split_level: 'suterräng',
};

/**
 * Short Swedish placement for admin: `0 · entré`, `+2`, `+1…+4`,
 * `normalplan`, `vind +4`, `takplan`, `—` when not placed.
 */
export const formatStoreyLevel = (level: StoreyLevel | null): string => {
  if (level === null) return '—';
  switch (level.kind) {
    case 'single':
      return level.index === 0 ? '0 · entré' : formatSignedIndex(level.index);
    case 'range':
      return `${formatSignedIndex(level.fromIndex)}…${formatSignedIndex(level.toIndex)}`;
    case 'typical':
      return 'normalplan';
    case 'roof':
      return 'takplan';
    case 'attic':
    case 'technical':
    case 'mezzanine':
    case 'split_level': {
      const name = SPECIAL_STOREY_LABEL[level.kind];
      return level.index === null
        ? name
        : `${name} ${formatSignedIndex(level.index)}`;
    }
    default:
      return assertUnreachable(level);
  }
};

/** `6 våningar`, `minst 3 våningar`. */
export const formatStoreyCount = (count: StoreyCount): string => {
  const noun = count.value === 1 ? 'våning' : 'våningar';
  switch (count.kind) {
    case 'exact':
      return `${count.value} ${noun}`;
    case 'at_least':
      return `minst ${count.value} ${noun}`;
    default:
      return assertUnreachable(count);
  }
};
